/**
 * Product Detail Page
 *
 * Displays product configurator for customizable products
 */

import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ProductConfigurator } from '@/features/ProductConfigurator';
import type { ProductVariant, AddOn } from '@/features/ProductConfigurator/types';
import { fetchProductConfiguration } from '@/services/configuratorApi';
import { useCartActions } from '@/hooks/useCart';
import { useUIStore } from '@/store/uiStore';

export const ProductDetail = () => {
  const { slug } = useParams<{ slug: string }>();
  const [variants, setVariants] = useState<ProductVariant[]>([]);
  const [addons, setAddons] = useState<AddOn[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { addItem } = useCartActions();
  const openCartDrawer = useUIStore((state) => state.openCartDrawer);

  useEffect(() => {
    loadProduct();
  }, [slug]);

  const loadProduct = async () => {
    try {
      setLoading(true);
      setError(null);

      // For now, hardcode to PadBox GS Essential
      const { variants: productVariants, addons: productAddons } =
        await fetchProductConfiguration('PADBOX-GS-ESS');

      setVariants(productVariants);
      setAddons(productAddons);
    } catch (err) {
      console.error('Error loading product:', err);
      setError('Failed to load product. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = async (configuration: any) => {
    const { selectedVariant, selectedAddOns, totalPrice } = configuration;

    if (!selectedVariant) return;

    try {
      // Add base product (color variant) to cart
      await addItem(selectedVariant.sku, 1);

      // Add each selected add-on as a separate line item
      for (const addon of selectedAddOns) {
        await addItem(addon.sku, 1);
      }

      // Show success message and open cart drawer
      alert(`Added to cart! Total: CAD $${totalPrice.toFixed(2)}`);
      openCartDrawer();
    } catch (error) {
      console.error('Add to cart error:', error);
      alert('Failed to add to cart. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-4xl mb-4">⏳</div>
          <div className="text-xl font-semibold text-gray-900">Loading product...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-4xl mb-4">❌</div>
          <div className="text-xl font-semibold text-gray-900 mb-2">Oops!</div>
          <div className="text-gray-600">{error}</div>
          <button
            onClick={loadProduct}
            className="mt-4 px-6 py-2 bg-brand-primary text-white rounded-lg hover:bg-brand-primary/90"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <ProductConfigurator
        productId="padbox-gs-essential"
        productName="PadBox GS [Essential]"
        productDescription="One Controller for every game. Powered by GP2040-CE for open-source control on PC, Switch, and more."
        variants={variants}
        addons={addons}
        onAddToCart={handleAddToCart}
      />
    </div>
  );
};
